import { useState } from 'react';
import { MovementListView } from './components/List/MovementListView';
import { DayFlowView } from './components/DayFlow/DayFlowView';
import { TimelineView } from './components/Timeline/TimelineView';
import { mockMovements } from './data/mockMovements';
import { Button } from '@/components/ui/button';
import { List, CalendarClock, ChevronLeft, Calendar as CalendarIcon, Filter, AlertCircle, ArrowDownToLine, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

export function MovementTimelinePage() {
  const [viewMode, setViewMode] = useState<'list' | 'dayflow' | 'timeline'>('dayflow');
  const navigate = useNavigate();

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden font-sans">
      {/* Top Navigation Bar: Premium FBO Layout */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between z-20 shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-1 group cursor-pointer" onClick={() => navigate('/demo')}>
            <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
              <ChevronLeft className="h-5 w-5 text-slate-600" />
            </div>
            <div className="flex flex-col ml-3">
              <div className="flex items-baseline gap-2">
                 <h1 className="text-lg font-semibold text-slate-900 leading-none">Schedule</h1>
                 <Badge variant="outline" className="font-mono text-xs bg-slate-50 text-slate-600 px-1.5 py-0 border-slate-200">KSUA</Badge>
              </div>
              <span className="text-xs text-slate-500 font-medium">Tue, Feb 18</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
             {/* Tri-View Toggle - Polished aesthetics */}
            <div className="flex bg-slate-100/80 p-1 rounded-lg border border-slate-200 shadow-sm">
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`h-8 px-4 gap-2 rounded-md transition-all duration-200 ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-700 font-semibold border border-slate-200/50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
                    onClick={() => setViewMode('list')}
                >
                    <List size={16} className={viewMode === 'list' ? 'text-blue-600' : ''} />
                    List
                </Button>
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`h-8 px-4 gap-2 rounded-md transition-all duration-200 ${viewMode === 'dayflow' ? 'bg-white shadow-sm text-blue-700 font-semibold border border-slate-200/50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
                    onClick={() => setViewMode('dayflow')}
                >
                    <ArrowDownToLine size={16} className={viewMode === 'dayflow' ? 'text-blue-600' : ''} />
                    Day Flow
                </Button>
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`h-8 px-4 gap-2 rounded-md transition-all duration-200 ${viewMode === 'timeline' ? 'bg-white shadow-sm text-blue-700 font-semibold border border-slate-200/50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`}
                    onClick={() => setViewMode('timeline')}
                >
                    <CalendarClock size={16} className={viewMode === 'timeline' ? 'text-blue-600' : ''} />
                    Timeline
                </Button>
            </div>

            <div className="h-8 w-px bg-slate-200 mx-2" />

            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-9 gap-2 text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900 font-medium bg-white">
                    <Filter size={15} className="text-slate-500" />
                    Filter
                </Button>
                <div className="relative">
                    <Button variant="default" size="sm" className="h-9 gap-2 bg-slate-900 hover:bg-slate-800 text-white font-medium pr-3">
                         <CalendarIcon size={15} className="text-slate-300" />
                         Today, Feb 18
                         <ChevronDown size={14} className="ml-1 opacity-70" />
                    </Button>
                </div>
            </div>
        </div>
      </div>
      
      {/* Premium Needs Attention Banner */}
      <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-200/60 px-6 py-2.5 flex items-center justify-between shrink-0 shadow-sm z-10">
          <div className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm">
                 <AlertCircle size={14} strokeWidth={3} />
              </div>
              <div className="flex items-baseline gap-2">
                  <span className="font-semibold tracking-tight text-amber-900 text-sm">Needs Attention</span>
                  <span className="text-amber-800 text-sm opacity-90">— N345PQ & N678RS stacking operation requires 3 crew members at 14:00</span>
              </div>
          </div>
          <Button variant="link" size="sm" className="text-amber-700 hover:text-amber-900 h-auto p-0 text-sm font-semibold tracking-wide">
              View Stacking Plan &rarr;
          </Button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative isolate">
        {/* Subtle background pattern for depth */}
        <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:24px_24px] opacity-40 mix-blend-multiply pointer-events-none" />
        
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden pt-4 pb-12 px-6 custom-scrollbar">
            {viewMode === 'list' && <MovementListView movements={mockMovements} />}
            {viewMode === 'dayflow' && <DayFlowView movements={mockMovements} />}
            {viewMode === 'timeline' && <TimelineView movements={mockMovements} />}
        </div>
      </div>
    </div>
  );
}
